/**
 * ProtectedStoreRecovery — the protected offline cache's unavailable and
 * quarantine states, with the administrator actions that resolve them.
 *
 * TCA-26100804 closing audit. This was ~110 lines of markup plus eight pieces of
 * state and three handlers inside ThisDevicePanel, which left that component
 * doing two unrelated jobs. Nothing here is shared with the rest of the panel:
 * every identifier it used was declared for it alone, so it moves whole rather
 * than being passed down as props.
 *
 * It owns its own state deliberately. Lifting it out as a presentational
 * component would have meant eighteen props, which is not an improvement over
 * leaving it where it was.
 */
import React, { useEffect, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

import useDesktopObservation from '@/hooks/useDesktopObservation';
import useDesktopCommand from '@/hooks/useDesktopCommand';
import { Button } from '@/components/ui/button';
import {
  localStoreCanRetry,
  localStoreNeedsAdministratorRecovery,
  localStoreRecoveryDetail,
} from '@/utils/localStoreRecovery';
import { LOCAL_STORE_RECOVERY_STATUS_EVENT } from '@/api/desktopSyncCoordinator';
import AdminConfirmationForm, { useAdminConfirmation } from './AdminConfirmationForm';

const RECOVERY_CONFIRMATION = 'REBUILD PROTECTED CACHE';
const CLEANUP_CONFIRMATION = 'DELETE RECOVERY QUARANTINE';
const RECOVERY_RESOURCE_LABELS = {
  orders: 'Orders', shop: 'Shop profile', directory: 'Sources and senders',
  runs: 'Runs', users: 'Users', heartbeat: 'Device heartbeat',
};

export default function ProtectedStoreRecovery() {
  const store = useDesktopObservation('getLocalStoreStatus', 'onLocalRuntimeStatusChanged', { invalidateEvent: true });
  const recovery = useDesktopObservation('getLocalStoreRecoveryStatus', null, {
    domEvent: LOCAL_STORE_RECOVERY_STATUS_EVENT, invalidateEvent: true,
    validate: value => value && typeof value.state === 'string' && typeof value.quarantinePreserved === 'boolean',
  });
  const command = useDesktopCommand('recovery');
  const localStoreStatus = store.status;
  const localStoreRecoveryStatus = recovery.status;
  const [showLocalStoreRecovery, setShowLocalStoreRecovery] = useState(false);
  const [showQuarantineCleanup, setShowQuarantineCleanup] = useState(false);
  const recoveryFields = useAdminConfirmation(RECOVERY_CONFIRMATION);
  const cleanupFields = useAdminConfirmation(CLEANUP_CONFIRMATION);
  const retryingLocalStore = command.busy;
  const recoveringLocalStore = command.busy;
  const cleaningQuarantine = command.busy;
  const { reset: resetRecovery } = recoveryFields;
  const { reset: resetCleanup } = cleanupFields;
  useEffect(() => {
    resetRecovery(); resetCleanup();
    setShowLocalStoreRecovery(false); setShowQuarantineCleanup(false);
  }, [command.scope, resetRecovery, resetCleanup]);
  const closeRecovery = () => { resetRecovery(); setShowLocalStoreRecovery(false); };
  const closeCleanup = () => { resetCleanup(); setShowQuarantineCleanup(false); };
  const canRecover = command.scope.admin && command.scope.cloud && store.phase === 'ready' && store.current()
    && localStoreNeedsAdministratorRecovery(localStoreStatus);
  const canClean = command.scope.admin && command.scope.cloud && recovery.phase === 'ready' && recovery.current()
    && localStoreRecoveryStatus?.state === 'ready_for_cleanup' && localStoreRecoveryStatus.quarantinePreserved === true;

  const run = async (method, fields, allowed, completed, message, close) => {
    if (!allowed || !command.scope.current() || typeof command.scope.bridge?.[method] !== 'function') return;
    if (!(method === 'cleanupLocalStoreQuarantine' ? recovery.current() : store.current())) return;
    if (fields && !fields.complete) return;
    const formCurrent = fields?.capture() || (() => true);
    const payload = fields?.credentials;
    await command.run(bridge => payload ? bridge[method](payload) : bridge[method](),
      async (result, error, current) => {
        await Promise.all([store.refresh(), recovery.refresh()]);
        if (!current() || !formCurrent()) return;
        if (!error && completed(result)) {
          close?.();
          toast.success(message);
        } else toast.error(error
          ? 'The recovery outcome could not be confirmed. Check native recovery before another attempt.'
          : 'The requested recovery step did not finish. Review the current status.');
      });
  };
  const handleRetryLocalStore = () => run('retryLocalStore', null,
    store.phase === 'ready' && store.current() && localStoreCanRetry(localStoreStatus),
    result => result?.state === 'ready', 'Protected offline storage is ready');
  const handleRecoverLocalStore = () => run('recoverLocalStore', recoveryFields,
    canRecover && showLocalStoreRecovery,
    result => result?.ok === true && result.state === 'ready' && result.recoveryStatus?.state === 'restoring',
    'Protected offline cache rebuilt', closeRecovery);
  const handleCleanupLocalStoreQuarantine = () => run('cleanupLocalStoreQuarantine', cleanupFields,
    canClean && showQuarantineCleanup,
    result => result?.state === 'completed' && result.quarantinePreserved === false,
    'Recovery quarantine deleted', closeCleanup);

  return (
    <>
      {(store.phase === 'unavailable' || store.phase === 'unsupported') && (
        <p role="status">Current protected-store status is unavailable. Reopen the desktop status before attempting recovery.</p>
      )}
      {recovery.phase === 'unavailable' && <p role="status">Restoration status could not be confirmed.</p>}
      {command.uncertain && <p role="status">The previous recovery outcome is unknown. Inspect native recovery or contact support before repeating it.</p>}
      {localStoreStatus?.state === 'unavailable' && (
        <div
          className={`rounded-xl border px-4 py-3 ${
            localStoreNeedsAdministratorRecovery(localStoreStatus)
              ? 'border-red-300 bg-red-50'
              : 'border-amber-300 bg-amber-50'
          }`}
          data-testid="protected-store-recovery-status"
        >
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <AlertTriangle className={`h-4 w-4 ${
                  localStoreNeedsAdministratorRecovery(localStoreStatus)
                    ? 'text-red-700'
                    : 'text-amber-700'
                }`} />
                <p className="text-sm font-semibold text-stone-800">
                  {localStoreNeedsAdministratorRecovery(localStoreStatus)
                    ? 'Protected cache recovery required'
                    : 'Protected offline storage is temporarily unavailable'}
                </p>
              </div>
              <p className="mt-1 text-xs text-stone-600">
                {localStoreRecoveryDetail(localStoreStatus)}
              </p>
            </div>
            {localStoreCanRetry(localStoreStatus) ? (
              <Button
                variant="outline"
                size="sm"
                onClick={handleRetryLocalStore}
                disabled={retryingLocalStore || !command.scope.admin}
                data-testid="protected-store-retry"
              >
                {retryingLocalStore ? 'Retrying…' : 'Retry'}
              </Button>
            ) : (
              <Button
                variant="destructive"
                size="sm"
                onClick={() => { resetRecovery(); setShowLocalStoreRecovery(value => !value); }}
                disabled={!canRecover || command.busy}
                data-testid="protected-store-rebuild"
              >
                Administrator recovery
              </Button>
            )}
          </div>
          {localStoreNeedsAdministratorRecovery(localStoreStatus) && (
            <p className="mt-3 border-t border-red-200 pt-3 text-xs text-red-800">
              Recovery preserves the inaccessible cache in quarantine. Its contents cannot be inspected, so unsynchronized work may be lost from active use.
            </p>
          )}
          {localStoreNeedsAdministratorRecovery(localStoreStatus) && showLocalStoreRecovery && (
            <AdminConfirmationForm
              fields={recoveryFields}
              testIdPrefix="protected-store-recovery"
              className="mt-3 space-y-3 border-t border-red-200 pt-3"
              instructionClassName="text-xs font-semibold text-red-900"
              instruction="Sign in again as the same online administrator, then type the exact confirmation."
              confirmLabel="Rebuild protected cache"
              busyLabel="Rebuilding…"
              busy={recoveringLocalStore}
              disabled={!canRecover}
              onConfirm={handleRecoverLocalStore}
              onCancel={closeRecovery}
            />
          )}
        </div>
      )}

      {localStoreRecoveryStatus?.quarantinePreserved && (
        <div className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3" data-testid="protected-store-restoration-status">
          <p className="text-sm font-semibold text-stone-800">Protected cache restoration</p>
          <p className="mt-1 text-xs text-stone-600">
            {localStoreRecoveryStatus.state === 'replacement_failed'
              ? 'The inaccessible generation remains preserved. The replacement could not start; restoration is incomplete.'
              : localStoreRecoveryStatus.state === 'ready_for_cleanup'
                ? 'Required resources report restoration complete. The inaccessible generation remains preserved until separate cleanup approval.'
                : 'The inaccessible generation remains preserved. Cloud-backed resource restoration is not yet complete.'}
          </p>
          <div className="mt-3 grid gap-1 text-xs md:grid-cols-2">
            {Object.entries(RECOVERY_RESOURCE_LABELS).map(([resource, label]) => (
              <div key={resource} data-testid={`protected-store-resource-${resource}`}>
                <span className="font-medium">{label}:</span>{' '}
                {localStoreRecoveryStatus.resources?.[resource]?.state || 'waiting'}
              </div>
            ))}
          </div>
          {localStoreRecoveryStatus.state === 'ready_for_cleanup' ? (
            <div className="mt-3 border-t border-blue-200 pt-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => { resetCleanup(); setShowQuarantineCleanup(value => !value); }}
                disabled={!canClean || command.busy}
                data-testid="protected-store-cleanup-open"
              >
                Delete recovery quarantine
              </Button>
              {showQuarantineCleanup && (
                <AdminConfirmationForm
                  fields={cleanupFields}
                  testIdPrefix="protected-store-cleanup"
                  className="mt-3 space-y-2"
                  instruction="This permanently removes the inaccessible prior cache. Reauthenticate as the same online administrator."
                  confirmLabel="Permanently delete quarantine"
                  busyLabel="Deleting…"
                  busy={cleaningQuarantine}
                  disabled={!canClean}
                  onCancel={closeCleanup}
                  onConfirm={handleCleanupLocalStoreQuarantine}
                />
              )}
            </div>
          ) : (
            <p className="mt-3 text-xs text-blue-800">Cleanup remains unavailable until every required resource is restored.</p>
          )}
        </div>
      )}
    </>
  );
}
